import { useRef, useEffect, useState, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import { useAtom } from 'jotai';
import {
  propertySearchOpenAtom,
  selectedPropertiesAtom,
} from '@/features/property-search';

interface PropertySearchButtonProps {
  onSearchChange: (search: string) => void;
  isHomePage: boolean;
}

export function PropertySearchButton({
  onSearchChange,
  isHomePage,
}: PropertySearchButtonProps) {
  const [open, setOpen] = useAtom(propertySearchOpenAtom);
  const [selectedProperties] = useAtom(selectedPropertiesAtom);
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Debounce search function
  const debouncedSearch = useMemo(() => {
    let timeoutId: NodeJS.Timeout;
    return (value: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => onSearchChange(value), 300);
    };
  }, [onSearchChange]);

  useEffect(() => {
    debouncedSearch(search);
  }, [search, debouncedSearch]);

  // Focus input when opened
  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open]);

  const getDisplayText = () => {
    if (isHomePage) {
      const count = selectedProperties.length;
      if (count === 0) {
        return 'All properties';
      }
      return `${count} ${count === 1 ? 'property' : 'properties'}`;
    }

    return selectedProperties.length > 0
      ? selectedProperties[0]?.name || 'Search properties'
      : 'Search properties';
  };

  return (
    <div className="relative">
      {open ? (
        <input
          ref={searchInputRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search properties..."
          className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      ) : (
        <button
          onClick={() => setOpen(true)}
          className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md text-sm text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 truncate"
        >
          {getDisplayText()}
        </button>
      )}

      <button
        onClick={() => setOpen(!open)}
        className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
      >
        <ChevronDown
          className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
    </div>
  );
}
